import { resolveGoogleOauthClientId, resolveWsHttpUrl } from '../lib/env.js';

export type GoogleOAuthStartResult = { ok: true } | { ok: false; reason: 'unavailable' | 'error' };

/**
 * F12 path 1 (Google OAuth) kickoff — shared by `/login` and `/signup`
 * (design/app mockups 13 and 14 both surface "Continue with Google").
 * Mints the login-CSRF `state` nonce via `GET /ws?oauthState=1`
 * (`plugins/websocket.ts`'s doc comment), then redirects the WHOLE page to
 * Google's authorization endpoint — the eventual `GET /auth/google/callback`
 * (`apps/server/src/routes/oauth.ts`) 302s back to this app's `/auth/callback`.
 *
 * Returns rather than throws: `'unavailable'` when no Google app is
 * configured for this environment (`VITE_GOOGLE_OAUTH_CLIENT_ID` unset) —
 * an honest notice beats a redirect that could only ever 503 at the
 * callback; `'error'` covers the state-mint round-trip failing. On success
 * this function never returns to its caller in practice (the browser
 * navigates away), but still resolves so callers don't need a rejection
 * handler for the ordinary "not configured" case.
 */
export async function startGoogleOAuth(): Promise<GoogleOAuthStartResult> {
  const clientId = resolveGoogleOauthClientId();
  if (!clientId) return { ok: false, reason: 'unavailable' };

  try {
    const httpBase = resolveWsHttpUrl();
    const res = await fetch(`${httpBase}?oauthState=1`, { credentials: 'include' });
    if (!res.ok) throw new Error(`oauthState mint failed: ${String(res.status)}`);
    const body: unknown = await res.json();
    const state = typeof body === 'object' && body !== null && 'state' in body ? String(body.state) : null;
    if (!state) throw new Error('oauthState mint: missing state in response body');

    const redirectUri = `${httpBase.replace(/\/ws$/, '')}/auth/google/callback`;
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      state,
      access_type: 'online',
      prompt: 'select_account',
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    return { ok: true };
  } catch {
    return { ok: false, reason: 'error' };
  }
}
